import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Trophy, Armchair, Target, Sparkles } from 'lucide-react';

const Features = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    {
      icon: Trophy,
      title: 'Tournament-Level Tables',
      description: 'Professional-grade tables with precision engineering for serious players.',
    },
    {
      icon: Armchair,
      title: 'Luxury Lounge Comfort',
      description: 'Premium leather seating and refined interiors for ultimate relaxation.',
    },
    {
      icon: Target,
      title: 'Premium Cue Collection',
      description: 'Hand-selected cues crafted for precision and performance.',
    },
    {
      icon: Sparkles,
      title: 'Elite Ambience',
      description: 'Cinematic lighting and sophisticated atmosphere for an elevated experience.',
    },
  ];

  return (
    <section
      id="features"
      ref={ref}
      className="py-20 md:py-32 bg-neutral-950 relative"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            className="font-cinzel text-4xl md:text-5xl font-semibold text-gold mb-4 tracking-wide"
            data-testid="features-title"
          >
            Why Choose Us
          </h2>
          <div className="w-24 h-1 bg-gold mx-auto"></div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="feature-card group"
                data-testid={`feature-card-${index}`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-full border-2 border-gold flex items-center justify-center group-hover:bg-gold transition-colors duration-300">
                    <Icon className="w-8 h-8 text-gold group-hover:text-black transition-colors duration-300" />
                  </div>

                  {/* Title */}
                  <h3 className="font-cinzel text-xl font-medium text-gold">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="font-montserrat text-sm text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
